/**
 * Level 6 — Lights Out.
 *
 * "A seemingly endless series of tight hallways made of a smooth, cold material
 * (likely concrete)", in permanent darkness and near-total silence. The wiki is
 * blunt about what is down here: *no entities have been found on this level*.
 * The entry that matters ends "Level 6 isn't dangerous because there's something
 * there. It's dangerous because there's nothing there."
 *
 * So this profile is built to be unreadable rather than complicated. It is a
 * plain rectilinear lattice of one-cell corridors on a fixed pitch: every
 * junction is a four-way, every leg between two junctions is the same two
 * metres, and there is not one room, alcove, pillar or change of width anywhere
 * on the level. A maze would be the wrong shape — a maze has a texture you can
 * learn, and Level 2 is already the level about that. This is a grid, which
 * means the player always knows exactly which way is north and it does them no
 * good whatsoever, because every square of it is identical and they can see six
 * metres.
 *
 * The lattice is built as a spanning tree first and then filled back in, so the
 * level is connected by construction and the missing legs are what stop it being
 * a perfect waffle you could count your way across.
 */

import { Cell } from '../../core/types';
import { addRegion, interiorRect, type GenSite, type Rect } from './site';
import { applyWrongness } from './wrongness';

/**
 * Cells between one junction and the next. Four is the smallest pitch that
 * leaves a leg long enough to be a *corridor* rather than a chamfered corner —
 * at 2.4 m cells that is a seven-metre run between crossings.
 */
const PITCH = 4;

/**
 * Chance a leg the spanning tree did not need is opened anyway.
 *
 * This is the only number that decides what the level feels like. At 0 it is a
 * perfect maze, which is Level 2. At 1 it is a perfect grid, which is legible.
 * High-but-not-one gives a grid with occasional missing legs: you keep walking
 * into the one crossing in twenty that does not go the way the last nineteen
 * did, and there is no way to tell which one you are at.
 */
const EXTRA_LINK = 0.62;

/** Blind stubs per hundred junctions: a corridor that runs two cells and stops. */
const STUBS_PER_100 = 9;

/** Union-find root with path compression. */
function findRoot(parent: Int32Array, a: number): number {
  let r = a;
  while (parent[r] !== r) r = parent[r]!;
  let cur = a;
  while (parent[cur] !== r) {
    const next = parent[cur]!;
    parent[cur] = r;
    cur = next;
  }
  return r;
}

/** Carve the Lights Out profile into `site`. */
export function generateLightsOut(site: GenSite): void {
  const { grid, rng } = site;
  const bounds = interiorRect(grid);
  const nx = Math.floor((bounds.w - 1) / PITCH) + 1;
  const ny = Math.floor((bounds.h - 1) / PITCH) + 1;
  if (nx < 3 || ny < 3) return;

  const nodeX = (i: number): number => bounds.x + i * PITCH;
  const nodeY = (j: number): number => bounds.y + j * PITCH;

  // Every junction exists. Only the legs between them are in question.
  for (let j = 0; j < ny; j++) {
    for (let i = 0; i < nx; i++) grid.set(nodeX(i), nodeY(j), Cell.Floor);
  }

  // Edge id = node * 2 + axis, axis 0 running +x and axis 1 running +y.
  const edges: number[] = [];
  for (let j = 0; j < ny; j++) {
    for (let i = 0; i < nx; i++) {
      const n = j * nx + i;
      if (i + 1 < nx) edges.push(n * 2);
      if (j + 1 < ny) edges.push(n * 2 + 1);
    }
  }
  rng.shuffle(edges);

  const parent = new Int32Array(nx * ny);
  for (let n = 0; n < parent.length; n++) parent[n] = n;
  const open = new Uint8Array(nx * ny * 2);

  // Pass one: a random spanning tree, so the level cannot come out in pieces.
  const spare: number[] = [];
  for (const e of edges) {
    const n = e >> 1;
    const other = (e & 1) === 0 ? n + 1 : n + nx;
    const a = findRoot(parent, n);
    const b = findRoot(parent, other);
    if (a === b) {
      spare.push(e);
      continue;
    }
    parent[a] = b;
    open[e] = 1;
  }
  // Pass two: put most of the grid back. Everything here is additive, so it can
  // only join the level together further.
  for (const e of spare) if (rng.chance(EXTRA_LINK)) open[e] = 1;

  for (let e = 0; e < open.length; e++) {
    if (!open[e]) continue;
    const n = e >> 1;
    const i = n % nx;
    const j = (n / nx) | 0;
    const horizontal = (e & 1) === 0;
    for (let k = 1; k < PITCH; k++) {
      if (horizontal) grid.set(nodeX(i) + k, nodeY(j), Cell.Floor);
      else grid.set(nodeX(i), nodeY(j) + k, Cell.Floor);
    }
  }

  carveStubs(site, nx, ny, nodeX, nodeY, open);
  labelDistricts(site, bounds);

  // Almost nothing, and only the one operation you can find in the dark. A
  // doorway with concrete behind it is felt rather than seen: you turn into what
  // your hand says is a corner and there is a wall a step and a half in.
  applyWrongness(site, {
    innerRooms: [0, 0],
    doorsToNothing: [10, 16],
    perforatedWalls: [0, 0],
    pillarsInDoorways: [0, 0],
    wallFins: [0, 0],
    tightPillarPatches: [0, 0],
  });
}

/**
 * Corridors that run two cells off a junction and stop.
 *
 * They go where a leg was not opened, so from the junction a stub is
 * indistinguishable from a way through until you are standing at the end of it.
 * On a lit level this would be a curiosity; here it is the level's only event.
 */
function carveStubs(
  site: GenSite,
  nx: number,
  ny: number,
  nodeX: (i: number) => number,
  nodeY: (j: number) => number,
  open: Uint8Array,
): void {
  const { grid, rng } = site;
  const count = Math.round((nx * ny * STUBS_PER_100) / 100);
  for (let s = 0; s < count; s++) {
    const i = rng.int(1, nx - 2);
    const j = rng.int(1, ny - 2);
    const n = j * nx + i;
    // The four legs off this junction, as (edge id, dx, dy).
    const legs: Array<[number, number, number]> = [
      [n * 2, 1, 0],
      [n * 2 + 1, 0, 1],
      [(n - 1) * 2, -1, 0],
      [(n - nx) * 2 + 1, 0, -1],
    ];
    rng.shuffle(legs);
    for (const [edge, dx, dy] of legs) {
      if (open[edge]) continue;
      // One cell short of the next junction, so the stub stays a dead end.
      for (let k = 1; k <= PITCH - 2; k++) {
        grid.set(nodeX(i) + dx * k, nodeY(j) + dy * k, Cell.Floor);
      }
      break;
    }
  }
}

/**
 * Coarse labels only. There is nothing on this level for a region to name — no
 * room, no hall, no closet — so it gets the same flat tiling Level 2's tangle
 * gets, which is enough for supplies and lighting to have somewhere to sit.
 */
function labelDistricts(site: GenSite, bounds: Rect): void {
  const tiles = 4;
  const tw = Math.floor(bounds.w / tiles);
  const th = Math.floor(bounds.h / tiles);
  for (let j = 0; j < tiles; j++) {
    for (let i = 0; i < tiles; i++) {
      addRegion(
        site,
        {
          x: bounds.x + i * tw,
          y: bounds.y + j * th,
          w: i === tiles - 1 ? bounds.w - i * tw : tw,
          h: j === tiles - 1 ? bounds.h - j * th : th,
        },
        'warren',
      );
    }
  }
}
